import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';

interface Contract {
  id: string;
  pavadinimas: string;
  sutartiesNumeris: string;
  galiojIki: string;
  suma: number;
  kiekis: number;
  kiekioTipas: 'EUR' | 'Kg' | 'Vienetai' | 'Bilietai' | 'Valandos';
  tipas: 'vidines' | 'partneriai';
  aprasymas?: string;
  statusas: 'aktyvus' | 'pasibaiges' | 'nutrauktas' | 'laukiantis' | 'archyvuotas';
  partneris?: string;
  partnerioKontaktai?: string;
  taikomosNuolaidos?: string;
  sutartymoData?: string;
  responsiblePerson?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ContractFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  contract?: Contract | null;
  contractType: 'vidines' | 'partneriai';
}

interface FormData {
  pavadinimas: string;
  sutartiesNumeris: string;
  galiojIki: Date | null;
  suma: string;
  kiekis: string;
  kiekioTipas: 'EUR' | 'Kg' | 'Vienetai' | 'Bilietai' | 'Valandos';
  aprasymas: string;
  statusas: 'aktyvus' | 'pasibaiges' | 'nutrauktas' | 'laukiantis' | 'archyvuotas';
  partneris: string;
  partnerioKontaktai: string;
  taikomosNuolaidos: string;
  sutartymoData: Date | null;
  atsakingasAsmuo: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContractFormDialog: React.FC<ContractFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  contract,
  contractType
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [users, setUsers] = useState<Array<{id: string; firstName: string; lastName: string}>>([]);
  
  const [formData, setFormData] = useState<FormData>({
    pavadinimas: '',
    sutartiesNumeris: '',
    galiojIki: null,
    suma: '',
    kiekis: '',
    kiekioTipas: 'EUR',
    aprasymas: '',
    statusas: 'aktyvus',
    partneris: '',
    partnerioKontaktai: '',
    taikomosNuolaidos: '',
    sutartymoData: null,
    atsakingasAsmuo: ''
  });

  const isEditing = !!contract;

  useEffect(() => {
    if (contract) {
      setFormData({
        pavadinimas: contract.pavadinimas,
        sutartiesNumeris: contract.sutartiesNumeris,
        galiojIki: new Date(contract.galiojIki),
        suma: contract.suma.toString(),
        kiekis: contract.kiekis.toString(),
        kiekioTipas: contract.kiekioTipas,
        aprasymas: contract.aprasymas || '',
        statusas: contract.statusas,
        partneris: contract.partneris || '',
        partnerioKontaktai: contract.partnerioKontaktai || '',
        taikomosNuolaidos: contract.taikomosNuolaidos || '',
        sutartymoData: contract.sutartymoData ? new Date(contract.sutartymoData) : null,
        atsakingasAsmuo: contract.responsiblePerson?.id || ''
      });
    } else {
      // Reset form for new contract
      setFormData({
        pavadinimas: '',
        sutartiesNumeris: '',
        galiojIki: null,
        suma: '',
        kiekis: '',
        kiekioTipas: 'EUR',
        aprasymas: '',
        statusas: 'aktyvus',
        partneris: '',
        partnerioKontaktai: '',
        taikomosNuolaidos: '',
        sutartymoData: null,
        atsakingasAsmuo: ''
      });
    }
    setError(null);
    setFormErrors({});
  }, [contract, open]);

  // Fetch users for responsible person dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(getApiUrl('/users'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.users || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.pavadinimas.trim()) {
      errors.pavadinimas = t('contracts.formValidation.nameRequired');
    }

    if (!formData.sutartiesNumeris.trim()) {
      errors.sutartiesNumeris = t('contracts.formValidation.numberRequired');
    }

    if (!formData.galiojIki) {
      errors.galiojIki = t('contracts.formValidation.validUntilRequired');
    }

    const suma = parseFloat(formData.suma);
    if (!formData.suma || isNaN(suma) || suma < 0) {
      errors.suma = t('contracts.formValidation.amountRequired');
    }

    const kiekis = parseFloat(formData.kiekis);
    if (!formData.kiekis || isNaN(kiekis) || kiekis < 0) {
      errors.kiekis = t('contracts.formValidation.quantityRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        pavadinimas: formData.pavadinimas.trim(),
        sutartiesNumeris: formData.sutartiesNumeris.trim(),
        galiojIki: formData.galiojIki?.toISOString(),
        suma: parseFloat(formData.suma),
        kiekis: parseFloat(formData.kiekis),
        kiekioTipas: formData.kiekioTipas,
        tipas: contractType,
        aprasymas: formData.aprasymas.trim() || undefined,
        statusas: formData.statusas,
        partneris: formData.partneris.trim() || undefined,
        partnerioKontaktai: formData.partnerioKontaktai.trim() || undefined,
        taikomosNuolaidos: contractType === 'partneriai' ? formData.taikomosNuolaidos.trim() : undefined,
        sutartymoData: formData.sutartymoData?.toISOString() || undefined,
        atsakingasAsmuo: formData.atsakingasAsmuo || undefined
      };

      const url = isEditing 
        ? getApiUrl(`/contracts/${contract.id}`)
        : getApiUrl('/contracts');
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.error === 'Contract number already exists') {
          setFormErrors({ sutartiesNumeris: t('contracts.formValidation.numberExists') });
          return;
        }
        throw new Error(result.error || `Failed to ${isEditing ? 'update' : 'create'} contract`);
      }

      onSubmit();
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} contract:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${isEditing ? 'update' : 'create'} contract`);
    } finally {
      setLoading(false);
    }
  };

  const quantityTypes = [
    { value: 'EUR', label: 'EUR' },
    { value: 'Kg', label: 'Kg' },
    { value: 'Vienetai', label: t('contracts.quantityTypes.Vienetai') },
    { value: 'Bilietai', label: t('contracts.quantityTypes.Bilietai') },
    { value: 'Valandos', label: t('contracts.quantityTypes.Valandos') }
  ] as const;

  const statusOptions = [
    { value: 'aktyvus', label: t('contracts.statuses.aktyvus') },
    { value: 'pasibaiges', label: t('contracts.statuses.pasibaiges') },
    { value: 'nutrauktas', label: t('contracts.statuses.nutrauktas') },
    { value: 'laukiantis', label: t('contracts.statuses.laukiantis') },
    { value: 'archyvuotas', label: t('contracts.statuses.archyvuotas') }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {isEditing ? t('contracts.editContract') : t('contracts.addContract')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({t(`contracts.contractTypes.${contractType}`)})
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                Pagrindinė informacija
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2,
                mt: 2
              }}>
                <TextField
                  fullWidth
                  label={t('contracts.contractName')}
                  value={formData.pavadinimas}
                  onChange={(e) => handleInputChange('pavadinimas', e.target.value)}
                  error={!!formErrors.pavadinimas}
                  helperText={formErrors.pavadinimas}
                  required
                />
                <TextField
                  fullWidth
                  label={t('contracts.contractNumber')}
                  value={formData.sutartiesNumeris}
                  onChange={(e) => handleInputChange('sutartiesNumeris', e.target.value)}
                  error={!!formErrors.sutartiesNumeris}
                  helperText={formErrors.sutartiesNumeris}
                  required
                />
                <DatePicker
                  label={t('contracts.validUntil')}
                  value={formData.galiojIki}
                  onChange={(date) => handleInputChange('galiojIki', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!formErrors.galiojIki,
                      helperText: formErrors.galiojIki
                    }
                  }}
                />
                <DatePicker
                  label="Sutartymo data"
                  value={formData.sutartymoData}
                  onChange={(date) => handleInputChange('sutartymoData', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.sutartymoData,
                      helperText: formErrors.sutartymoData
                    }
                  }}
                />
                <FormControl fullWidth error={!!formErrors.statusas}>
                  <InputLabel required>{t('contracts.status')}</InputLabel>
                  <Select
                    value={formData.statusas}
                    label={t('contracts.status')}
                    onChange={(e) => handleInputChange('statusas', e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.statusas && (
                    <FormHelperText>{formErrors.statusas}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Financial Information */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Finansinė informacija
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 2,
                mt: 2
              }}>
                <TextField
                  fullWidth
                  label={t('contracts.amount')}
                  value={formData.suma}
                  onChange={(e) => handleInputChange('suma', e.target.value)}
                  error={!!formErrors.suma}
                  helperText={formErrors.suma}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label={t('contracts.quantity')}
                    value={formData.kiekis}
                    onChange={(e) => handleInputChange('kiekis', e.target.value)}
                    error={!!formErrors.kiekis}
                    helperText={formErrors.kiekis}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                    sx={{ flex: 1 }}
                  />
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>{t('contracts.quantityType')}</InputLabel>
                    <Select
                      value={formData.kiekioTipas}
                      label={t('contracts.quantityType')}
                      onChange={(e) => handleInputChange('kiekioTipas', e.target.value)}
                    >
                      {quantityTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Company Information (for internal contracts) */}
            {contractType === 'vidines' && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Įmonės informacija
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2,
                  mt: 2
                }}>
                  <TextField
                    fullWidth
                    label="Įmonė"
                    value={formData.partneris}
                    onChange={(e) => handleInputChange('partneris', e.target.value)}
                    error={!!formErrors.partneris}
                    helperText={formErrors.partneris}
                    placeholder="Įmonės pavadinimas"
                  />
                  <TextField
                    fullWidth
                    label="Įmonės kontaktai"
                    value={formData.partnerioKontaktai}
                    onChange={(e) => handleInputChange('partnerioKontaktai', e.target.value)}
                    multiline
                    rows={2}
                    placeholder="El. paštas, telefonas, adresas..."
                  />
                </Box>
              </Box>
            )}

            {/* Partner Information (only for partner contracts) */}
            {contractType === 'partneriai' && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Partnerio informacija
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2,
                  mt: 2
                }}>
                  <TextField
                    fullWidth
                    label={t('contracts.partner')}
                    value={formData.partneris}
                    onChange={(e) => handleInputChange('partneris', e.target.value)}
                    error={!!formErrors.partneris}
                    helperText={formErrors.partneris}
                  />
                  <TextField
                    fullWidth
                    label={t('contracts.partnerContacts')}
                    value={formData.partnerioKontaktai}
                    onChange={(e) => handleInputChange('partnerioKontaktai', e.target.value)}
                    multiline
                    rows={2}
                    placeholder="El. paštas, telefonas, adresas..."
                  />
                  <TextField
                    fullWidth
                    label="Taikomos nuolaidos"
                    value={formData.taikomosNuolaidos}
                    onChange={(e) => handleInputChange('taikomosNuolaidos', e.target.value)}
                    placeholder="Taikomos nuolaidos..."
                  />
                </Box>
              </Box>
            )}

            {/* Responsible Person */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Atsakingas asmuo
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth error={!!formErrors.atsakingasAsmuo}>
                  <InputLabel>Atsakingas asmuo</InputLabel>
                  <Select
                    value={formData.atsakingasAsmuo}
                    label="Atsakingas asmuo"
                    onChange={(e) => handleInputChange('atsakingasAsmuo', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Nepriskirtas</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.atsakingasAsmuo && (
                    <FormHelperText>{formErrors.atsakingasAsmuo}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Description */}
            <TextField
              fullWidth
              label={t('contracts.description')}
              value={formData.aprasymas}
              onChange={(e) => handleInputChange('aprasymas', e.target.value)}
              multiline
              rows={3}
              placeholder="Papildoma informacija apie sutartį..."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('contracts.actions.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? t('contracts.loading') : 
             isEditing ? t('contracts.actions.update') : t('contracts.actions.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ContractFormDialog;
